import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import {
	ATELIER_ETAT_DEVIS_A_FAIRE,
	ATELIER_ETAT_DEVIS,
	ATELIER_ETAT_DEVIS_ACCEPTE,
} from "../../../utilities/Addons"
import { getBaseAtelier } from "../../../utilities/defaultObjects"
import { postAtelier } from "../../../store/actions/atelierActions"
import { URL } from "../../../store/actions/depannageActions"
import { downloadPdfFromResponse } from "../../basics/button/AppBtnDownloadPdf"
import { toastApiError } from "../../../store/actions/toastActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"

const vehLabel = (v) =>
	`${v.plaques || ""} ${v.marque || ""} ${v.modele || ""}`
		.replace(/\s+/g, " ")
		.trim() || "Véhicule"
const adrLabel = (a) =>
	`${a.numero || ""} ${a.rue || ""} ${a.code_postal || ""} ${a.localite || ""}`
		.replace(/\s+/g, " ")
		.trim() || "Adresse"

// Crée une fiche atelier pré-remplie depuis un client (véhicule + adresse).
// Reprend le workflow legacy : Diagnostique = postAtelier + PDF + redirection ;
// Devis / Devis accepté = redirection /atelier/fiche/0 (from:"clients" + extraData).
const ClientToAtelierModalV2 = ({ entite, onClose }) => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { token } = useSelector((s) => s.navInfo.login)
	const savedVehicules = useMemo(
		() => (entite.vehicules || []).filter((v) => v.id != null),
		[entite.vehicules],
	)
	const savedAdresses = useMemo(
		() => (entite.adresses || []).filter((a) => a.id != null),
		[entite.adresses],
	)
	const [selVeh, setSelVeh] = useState(savedVehicules.length === 1 ? 0 : -1)
	const [selAdr, setSelAdr] = useState(savedAdresses.length === 1 ? 0 : -1)
	const [creating, setCreating] = useState(false)

	const missing = savedVehicules.length === 0 || savedAdresses.length === 0
	const invalid = selVeh === -1 || selAdr === -1

	const goToNewFiche = (etat) => {
		history.push({
			pathname: "/atelier/fiche/0",
			from: "clients",
			extraData: {
				entiteId: entite.id,
				vehiculeId: savedVehicules[selVeh].id,
				adresseId: savedAdresses[selAdr].id,
				etat,
			},
		})
	}

	const createDiagnostique = () => {
		setCreating(true)
		const a = getBaseAtelier()
		a.etat = ATELIER_ETAT_DEVIS_A_FAIRE
		a.vehicule = savedVehicules[selVeh].id
		a.adresse = savedAdresses[selAdr].id
		a.entite = entite.id
		dispatch(postAtelier(JSON.stringify({ atelier: a }))).then((json) => {
			if (!json || !json.atelier) {
				setCreating(false)
				return
			}
			fetch(
				`${URL}api/pdf?id=${json.atelier.id}&type=atelier&pdf_type=diagnostique`,
				{ headers: { Authorization: `Bearer ${token}` } },
			)
				.then((response) => downloadPdfFromResponse(response))
				// PDF raté : la fiche existe déjà, on y va quand même (sinon le
				// modal reste bloqué en `creating` sans aucune sortie).
				.catch((err) => dispatch(toastApiError(err)))
				.then(() => {
					history.push(`/atelier/fiche/${json.atelier.id}`)
				})
		})
	}

	const selectField = (label, options, value, setter, fmt) => (
		<div style={gs.fieldRow}>
			<label style={gs.label}>{label}</label>
			{options.length > 1 ? (
				<select
					style={gs.input}
					value={value}
					onChange={(e) => setter(parseInt(e.target.value))}
				>
					<option value={-1}>—</option>
					{options.map((o, i) => (
						<option key={o.id} value={i}>
							{fmt(o)}
						</option>
					))}
				</select>
			) : (
				<span style={{ fontSize: 13, color: COLORS.text }}>
					{fmt(options[0])}
				</span>
			)}
		</div>
	)

	return (
		<CustomModal
			title="Nouvelle fiche atelier"
			subtitle={
				entite.label || `${entite.prenom || ""} ${entite.nom || ""}`.trim()
			}
			onClose={creating ? () => {} : onClose}
			showCloseButton={!creating}
			buttons={[
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: onClose,
					disabled: creating,
				},
			]}
		>
			{missing ? (
				<p style={{ margin: 0, fontSize: 13, color: COLORS.textMuted }}>
					Ce client doit avoir au moins un véhicule et une adresse
					enregistrés pour créer une fiche atelier.
				</p>
			) : (
				<div style={gs.fieldGroup}>
					{selectField(
						"Véhicule à réparer",
						savedVehicules,
						selVeh,
						setSelVeh,
						vehLabel,
					)}
					{selectField(
						"Adresse de facturation",
						savedAdresses,
						selAdr,
						setSelAdr,
						adrLabel,
					)}
					<div
						style={{
							display: "flex",
							gap: 10,
							marginTop: 10,
							flexWrap: "wrap",
						}}
					>
						<button
							type="button"
							style={{
								...gs.btnPrimary,
								...((invalid || creating) && {
									opacity: 0.5,
									cursor: "not-allowed",
								}),
							}}
							onClick={createDiagnostique}
							disabled={invalid || creating}
						>
							{creating ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Diagnostique"
							)}
						</button>
						<button
							type="button"
							style={{
								...styles.secondary,
								...((invalid || creating) && {
									opacity: 0.5,
									cursor: "not-allowed",
								}),
							}}
							onClick={() => goToNewFiche(ATELIER_ETAT_DEVIS)}
							disabled={invalid || creating}
						>
							Devis
						</button>
						<button
							type="button"
							style={{
								...styles.secondary,
								...((invalid || creating) && {
									opacity: 0.5,
									cursor: "not-allowed",
								}),
							}}
							onClick={() =>
								goToNewFiche(ATELIER_ETAT_DEVIS_ACCEPTE)
							}
							disabled={invalid || creating}
						>
							Devis accepté
						</button>
					</div>
				</div>
			)}
		</CustomModal>
	)
}

const styles = {
	secondary: {
		padding: "10px 18px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 13,
		fontWeight: 500,
		fontFamily: "inherit",
		cursor: "pointer",
	},
}

export default ClientToAtelierModalV2
